import axios from 'axios';
import i18next from 'i18next';
import { useParams } from 'react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fileNotFound } from '../../assets/images/icons';
import { truncateString } from '../../utils/truncateString';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { ShoppingBagIcon, EnvelopeIcon, MapPinIcon, PhoneIcon, UserIcon, CalendarDaysIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import { DateFormat } from '../../utils/DateFormat';
import UpdateStatus from './UpdateStatus';

export default function OrderDetails() {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const localhost = process.env.REACT_APP_LOCALHOST;
  const localimg = process.env.REACT_APP_LOCALHOST_IMG;
  const [deliveryStatus, setDeliveryStatus] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [receiptData, setReceiptData] = useState([]);
  const [user, setUser] = useState([]);
  const [address, setAddress] = useState([]);
  const [ocData, setOcData] = useState([]);
  const [orders, setOrders] = useState([]);
  const [firstLoading, setFirstLoading] = useState(true);
  const [secondLoading, setSecondLoading] = useState(true);
  const { reference } = useParams();

  const fetchDetails = async () => {
    try {
      const res = await axios.get(`${localhost}admin/get-order-details/${reference}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setReceiptData(res.data);
      setUser(res.data.user);
      setAddress(res.data.address);
      setOcData(res.data.ocData);
      setFirstLoading(false);

      const dataa = res.data.orders;
      const updatedData = dataa.map(item => {
        const color_name = item.color_name ? JSON.parse(item.color_name) : null;
        const imgs = item.imgs ? JSON.parse(item.imgs) : null;
        const name = item.name ? JSON.parse(item.name) : null;
        return { ...item, color_name, imgs, name };
      });
      setOrders(updatedData);
      setSecondLoading(false);
    } catch (err) {
      // console.error(err);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, []);


  const total = orders.reduce((sum, order) => sum + parseFloat(order.amount), 0);

  const ProductCard = ({ product }) => {
    return (
      <div className="bg-white p-2.5 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-start gap-4">
          <img loading="lazy" className="h-20 w-20 rounded-md object-cover" src={product.imgs ? localimg + product.imgs[0] : fileNotFound} alt={product.slug} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col items-start">
              <a href={`/product/${product.slug}`} className="text-sm font-medium text-black hover:underline">
                {truncateString(product.name[i18next.language], 25)}
              </a>
            </div>
            <dl className="mt-2 text-sm text-gray-500 space-y-1">
              <div className="flex justify-between">
                <dt>ID</dt>
                <dd className='text-black'># {product.num_product}</dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("price")}</dt>
                <dd className="font-medium text-black">
                  {parseFloat(product.price).toFixed(2)} MAD
                </dd>
              </div>
              <div className="flex flex-col justify-between">
                <dt>{t("order-date")}</dt>
                <dd className='text-end text-black'><DateFormat dateString={product.order_date} /></dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("size")}</dt>
                <dd className='text-black'>{product.size_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("color")}</dt>
                <dd className='text-black'>
                  {product.color_name ? (
                    <div data-tooltip-id="my-tooltip" data-tooltip-content={product.color_name[i18next.language]}
                      className="border border-gray-300 size-6 rounded-full" style={{ background: product.hex }} />
                  ) : (
                    "--"
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("quantity")}</dt>
                <dd className='text-black'>{product.quantity}</dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("total")}</dt>
                <dd className='text-black'>{parseFloat(product.amount).toFixed(2)} MAD</dd>
              </div>
            </dl>
          </div>
        </div >
        <div className="flex justify-end mt-2 gap-1">
          {/*  */}
        </div>
      </div >
    );
  };

  return (
    <div className="antialiased">
      <div className="max-w-7xl mx-auto">
        <div className="w-full flex justify-start mb-2">
          <a href="/admin/orders" className="flex gap-1 items-center text-sm bg-black hover:bg-black/80 text-white pl-2 pr-3 py-2 rounded-md">
            <ArrowLeftIcon className="size-4" /> {t("back")}
          </a>
        </div>
        <div className="bg-white shadow-sm ring-1 ring-black/5 rounded-lg">
          <div className="sticky top-0 border-b md:p-4 p-2 flex justify-between items-center">
            <h2 className="text-xl max-md:text-lg font-bold">{t("order-details")}</h2>
          </div>

          <div className="space-y-4 md:p-6 p-2">
            <div className="space-y-4">
              <h3 className="font-semibold md:text-lg text-base">{t("customer-info")}</h3>
              {firstLoading ? (
                <div className="border border-orange-200 rounded-lg shadow-sm overflow-hidden bg-orange-50 min-h-[30vh] flex items-center justify-center">
                  <div className="loading-spinner-40" />
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="max-md:col-span-2 bg-orange-50 p-4 border rounded-lg shadow-sm hover:shadow-md border-orange-100">
                    <p className="text-orange-600 flex items-center gap-1">
                      <ShoppingBagIcon className="size-4 text-orange-500" />
                      {t("reference")}
                    </p>
                    <p># {reference}</p>
                  </div>
                  <div className="max-md:col-span-2 bg-orange-50 p-4 border rounded-lg shadow-sm hover:shadow-md border-orange-100">
                    <p className="text-orange-600 flex items-center gap-1">
                      <UserIcon className="size-4 text-orange-500" />
                      {t("fullname")}
                    </p>
                    <p>{user.name}</p>
                  </div>
                  <div className="max-md:col-span-2 bg-orange-50 p-4 border rounded-lg shadow-sm hover:shadow-md border-orange-100">
                    <p className="text-orange-600 flex items-center gap-1">
                      <EnvelopeIcon className="size-4 text-orange-500" />
                      {t("email")}
                    </p>
                    <p>{user.email}</p>
                  </div>
                  <div className="max-md:col-span-2 bg-orange-50 p-4 border rounded-lg shadow-sm hover:shadow-md border-orange-100">
                    <p className="text-orange-600 flex items-center gap-1">
                      <PhoneIcon className="size-4 text-orange-500" />
                      {t("phone")}
                    </p>
                    <p>{user.phone}</p>
                  </div>
                  <div className="col-span-2 bg-orange-50 p-4 border rounded-lg shadow-sm hover:shadow-md border-orange-100">
                    <p className="text-orange-600 flex items-center gap-1">
                      <MapPinIcon className="size-4 text-orange-500" />
                      {t("address")}
                    </p>
                    <p>{address.address_line1}</p>
                    <p>
                      {address.city}, {address.region} {address.zip_code}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Order Items */}
            <div className="space-y-4">
              <h3 className="font-semibold md:text-lg text-base">{t("order-items")}</h3>
              {secondLoading ? (
                <div className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white min-h-[25vh] flex items-center justify-center">
                  <div className="loading-spinner-40" />
                </div>
              ) : (
                <div className="space-y-4">
                  <table className="max-md:hidden min-w-full divide-y divide-gray-200 border shadow-sm rounded-md">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">No</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("product")}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("price")}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("size")}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("color")}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("quantity")}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orders.map((item, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 whitespace-nowrap">{index + 1}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center">
                              <img loading="lazy" className="h-16 w-16 rounded-md object-cover" src={item.imgs ? localimg + item.imgs[0] : fileNotFound} alt={item.slug} />
                              <div className="ml-4">
                                <a href={`/product/${item.slug}`} className="hover:underline text-sm font-medium text-black" data-tooltip-id="my-tooltip" data-tooltip-content={item.name[i18next.language]}>
                                  {truncateString(item.name[i18next.language], 30)}
                                </a>
                                <div className="text-sm text-gray-500">ID : {item.num_product}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{parseFloat(item.price).toFixed(2)} MAD</td>
                          <td className="px-3 py-2 whitespace-nowrap">{item.size_name}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {item.color_name ? (
                              <div data-tooltip-id="my-tooltip" data-tooltip-content={item.color_name[i18next.language]}
                                className="border border-gray-300 size-6 rounded-full" style={{ background: item.hex }} />
                            ) : (
                              "--"
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{item.quantity}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{parseFloat(item.amount).toFixed(2)} MAD</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="md:hidden space-y-2">
                    {orders.map((product, index) => (
                      <ProductCard key={index} product={product} />
                    ))}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center font-bold md:text-lg text-base">
                      <p>Total</p>
                      <p>{total.toFixed(2)} MAD</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {
              firstLoading ? (
                <div className="border border-orange-200 rounded-lg shadow-sm overflow-hidden bg-orange-50 min-h-20 flex items-center justify-center">
                  <div className="loading-spinner-40" />
                </div>
              ) : (
                <div>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="bg-orange-50 p-4 rounded-lg border shadow-sm hover:shadow-md border-orange-100">
                      <p className="capitalize text-orange-600 flex items-center gap-1">
                        <CalendarDaysIcon className="size-4 text-orange-500" />
                        {t("order-date")}
                      </p>
                      <p>
                        <DateFormat dateString={ocData.order_date} />
                      </p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg border shadow-sm hover:shadow-md border-orange-100">
                      <p className="capitalize text-orange-600 flex items-center gap-1">
                        <ArrowPathIcon className="size-4 text-orange-500" />
                        {t("status")}
                      </p>
                      <p>{t(ocData.collection_status)}</p>
                    </div>
                  </div>
                </div>
              )
            }

            <div>
              <UpdateStatus
                referenceProp={reference}
                token={token}
                localhost={localhost} t={t}
                deliveryStatus={deliveryStatus}
                setDeliveryStatus={setDeliveryStatus}
                orderStatus={orderStatus}
                setOrderStatus={setOrderStatus}
              />
            </div>

            {/* <div className='min-h-[20vh] border-2 border-red-500'>
              <OrderReceipt reference={reference} localhost={localhost} />
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

